import React from 'react';
import styled from 'styled-components';

const PolicyContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  color: ${({ theme }) => theme.text};
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.textStrong};
`;

const MetaInfo = styled.div`
  margin-bottom: 2rem;
  font-size: 0.9rem;
  opacity: 0.8;
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const CompanyInfo = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  
  p {
    margin-bottom: 0.5rem;
  }
`;

const Content = styled.div`
  font-size: 1rem;
  line-height: 1.7;

  h2 {
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem;
    color: ${({ theme }) => theme.textStrong};
  }
  
  h3 {
    font-size: 1.2rem;
    margin: 1.5rem 0 1rem;
    color: ${({ theme }) => theme.textStrong};
  }

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
  
  a {
    color: var(--primary);
    text-decoration: none;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      width: 0;
      height: 1px;
      bottom: -1px;
      left: 0;
      background-color: var(--primary);
      transition: width 0.3s ease;
    }
    
    &:hover {
      &:after {
        width: 100%;
      }
    }
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.textStrong};
`;

const PrivacyPolicy = () => {
  return (
    <PolicyContainer>
      <Title>Privacy Policy</Title>
      
      <MetaInfo>
        <p>Effective Date: April 14, 2025</p>
        <p>Last Updated: May 5, 2025</p>
      </MetaInfo>
      
      <Content>
        <p>
          This Privacy Policy explains how Mystream Limited (referred to as "we", "us", or "our") collects, uses, and protects your information when you use the 1cal: AI Food Calorie Counter mobile application (the "Application").
        </p>
        
        <CompanyInfo>
          <p>Mystream Limited is a company registered in Cyprus:</p>
          <p><strong>Company Name:</strong> Mystream Limited</p>
          <p><strong>Registration Number:</strong> HE423185</p>
          <p><strong>Registered Address:</strong> Spirou Kyprianou, 78, MAGNUM BUSINESS CENTER, 3rd floor, 3076, Limassol, Cyprus</p>
          <p><strong>Contact Email:</strong> <a href="mailto:support@1cal.ai">support@1cal.ai</a></p>
        </CompanyInfo>
        
        <p>
          By using our Application, you consent to the processing of your data as described in this Privacy Policy.
        </p>
        
        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          <p>We may collect the following categories of data when you use the Application:</p>
          
          <h3>Automatically Collected:</h3>
          <ul>
            <li>Device and OS information</li>
            <li>IP address</li>
            <li>Usage data (screen views, session time)</li>
            <li>Approximate location (from network, not GPS)</li>
            <li>Crash logs and performance analytics</li>
          </ul>
          
          <h3>User-Provided:</h3>
          <ul>
            <li>Email or name (if submitted voluntarily)</li>
            <li>In-app purchases or subscription data (via Apple/Google/Adapty)</li>
          </ul>
          
          <p>We do not collect:</p>
          <ul>
            <li>Sensitive personal data (e.g., health, biometric)</li>
            <li>Exact geolocation</li>
            <li>Personal data from children under 13</li>
          </ul>
        </Section>
        
        <Section>
          <SectionTitle>2. Legal Basis for Processing (GDPR)</SectionTitle>
          <p>We process personal data under the following legal grounds:</p>
          <ul>
            <li>Consent (e.g., marketing, optional analytics)</li>
            <li>Contractual necessity (for app functionality)</li>
            <li>Legal obligation (when required by law)</li>
            <li>Legitimate interests (e.g., app improvement, fraud prevention)</li>
          </ul>
        </Section>
        
        <Section>
          <SectionTitle>3. Purpose of Data Use</SectionTitle>
          <p>We use collected data to:</p>
          <ul>
            <li>Provide and support the Application</li>
            <li>Monitor and improve performance</li>
            <li>Communicate essential updates or support</li>
            <li>Personalize user experience (non-intrusively)</li>
            <li>Meet legal or contractual obligations</li>
          </ul>
        </Section>
        
        <Section>
          <SectionTitle>4. Third-Party Services</SectionTitle>
          <p>We may share limited data with service providers:</p>
          <ul>
            <li>Analytics & performance: Google Play Services, Amplitude</li>
            <li>Subscription and billing: Adapty</li>
            <li>Legal compliance or protection of rights</li>
          </ul>
          <p>These providers are bound by confidentiality and data protection agreements.</p>
        </Section>
        
        <Section>
          <SectionTitle>5. International Transfers</SectionTitle>
          <p>Your data may be processed outside the EEA. We ensure appropriate safeguards, such as Standard Contractual Clauses, are in place to protect your rights.</p>
        </Section>
        
        <Section>
          <SectionTitle>6. User Rights Under GDPR</SectionTitle>
          <p>If you reside in the EEA, you may:</p>
          <ul>
            <li>Request access, correction, or deletion of your data</li>
            <li>Object to or restrict processing</li>
            <li>Withdraw consent at any time</li>
            <li>Lodge a complaint with your local data protection authority</li>
          </ul>
          <p>Contact: <a href="mailto:support@1cal.ai">support@1cal.ai</a></p>
        </Section>
        
        <Section>
          <SectionTitle>7. California Privacy Rights (CCPA/CPRA)</SectionTitle>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li>Know what data we collect and how we use it</li>
            <li>Request deletion of your personal data</li>
            <li>Opt out of the sale or sharing of personal data</li>
            <li>Receive equal service regardless of exercising your rights</li>
          </ul>
          <p>We do not sell personal data under CCPA definitions.</p>
          <p>To submit a request, email <a href="mailto:support@1cal.ai">support@1cal.ai</a> with "California Privacy Rights" in the subject.</p>
        </Section>
        
        <Section>
          <SectionTitle>8. Data Retention</SectionTitle>
          <p>We retain your data as long as reasonably necessary for app functionality, legal compliance, or account support. You may request deletion by contacting us.</p>
        </Section>
        
        <Section>
          <SectionTitle>9. Security</SectionTitle>
          <p>We apply administrative, technical, and organizational measures to protect personal data, including encryption, access control, and monitoring.</p>
        </Section>
        
        <Section>
          <SectionTitle>10. Children's Privacy</SectionTitle>
          <p>We do not knowingly collect data from children under 13. If we become aware that such data has been submitted, we will delete it immediately. Parents may contact <a href="mailto:support@1cal.ai">support@1cal.ai</a> to request removal.</p>
        </Section>
        
        <Section>
          <SectionTitle>11. Cookies and Tracking Technologies</SectionTitle>
          <p>If the Application displays embedded web content (e.g., help pages, external forms), we may use cookies or similar tracking technologies to enhance functionality. These may include:</p>
          <ul>
            <li>Essential cookies for navigation</li>
            <li>Analytics cookies for performance tracking (non-personal)</li>
          </ul>
          <p>You may manage cookies via your browser or device settings.</p>
        </Section>
        
        <Section>
          <SectionTitle>12. Business Transfers</SectionTitle>
          <p>In the event of a sale, merger, or acquisition, your information may be transferred to the successor entity, subject to the same commitments outlined in this Privacy Policy.</p>
        </Section>
        
        <Section>
          <SectionTitle>13. Changes to This Policy</SectionTitle>
          <p>We may update this Privacy Policy to reflect changes in law or practices. Users will be notified of significant changes via in-app notices or updates to this page.</p>
        </Section>
        
        <Section>
          <SectionTitle>14. Governing Law and Jurisdiction</SectionTitle>
          <p>This Privacy Policy shall be governed by and construed in accordance with the laws of the Republic of Cyprus. Any disputes shall be subject to the exclusive jurisdiction of the courts of Limassol, Cyprus.</p>
        </Section>
        
        <Section>
          <SectionTitle>15. Contact Us</SectionTitle>
          <p>For any privacy-related questions or requests, please contact us at:</p>
          <p>Email: <a href="mailto:support@1cal.ai">support@1cal.ai</a></p>
          <p>Mystream Limited<br />
          Spirou Kyprianou, 78, MAGNUM BUSINESS CENTER<br />
          3rd floor, 3076, Limassol, Cyprus</p>
        </Section>
      </Content>
    </PolicyContainer>
  );
};

export default PrivacyPolicy; 